import { GoogleMap as Map, Marker } from '@react-google-maps/api'
import { useCallback, useMemo } from 'react'

import { GoogleMapOptions } from 'lib/customGoogleMapOptions'
import useCheckBreakpoint from 'lib/checkBreakpoint'

import styles from './LocationUnitGoogleMap.module.scss'
import { getMarkerZIndex } from './LocationUnitGoogleMap.utils'
import {
  getCoordinate,
  isLockerLocationUnit,
  isSpaceLocationUnit,
  LocationUnit,
  LocationUnitId,
} from 'state/ducks/locationUnit/models'
import LocationUnitCard from 'components/domains/locationUnit/LocationUnitCard'

const GENERAL_SPACE_PIN = {
  active: '/pin_active.png',
  inactive: '/pin_inactive.png',
}

const LOCKER_PIN = {
  active: '/marker_coin_locker.svg',
  inactive: '/marker_gray_coin_locker.svg',
}

interface Props {
  zoomLabel: number
  locationUnits: LocationUnit[]
  mapRef: any //wip
  initGoogleMap: (map) => void
  onDragMap: () => void
  onChangeZoomMap: () => void
  onPressMarker: (locationUnit: LocationUnit) => void
  selectedLocationUnitId: LocationUnitId
}

const GoogleMap: React.FC<Props> = ({
  zoomLabel,
  locationUnits,
  mapRef,
  initGoogleMap,
  onDragMap,
  onChangeZoomMap,
  onPressMarker,
  selectedLocationUnitId,
}: Props): JSX.Element => {
  const { isMobile, isTablet } = useCheckBreakpoint()

  const _googleMapOptions: google.maps.MapOptions = {
    fullscreenControl: false,
    zoomControl: (!isTablet && !isMobile) || false,
    zoomControlOptions: { position: 1 },
    styles: GoogleMapOptions,
    disableDefaultUI: true,
    minZoom: 6,
    maxZoom: 16,
  }

  const selectedLocationUnit = useMemo(() => {
    const selectedUnit = locationUnits.find((locationUnit) => {
      if (
        locationUnit.type === selectedLocationUnitId.type &&
        locationUnit.encryptedId === selectedLocationUnitId.encryptedId
      )
        return locationUnit
    })

    return selectedUnit
  }, [locationUnits, selectedLocationUnitId])

  const getMarkerIcon = useCallback((isAvailable, mapMarker, isLocker) => {
    if (mapMarker) {
      return isAvailable ? mapMarker.active_image_url : mapMarker.inactive_image_url
    }
    if (isLocker) {
      return isAvailable ? LOCKER_PIN.active : LOCKER_PIN.inactive
    }
    return isAvailable ? GENERAL_SPACE_PIN.active : GENERAL_SPACE_PIN.inactive
  }, [])

  return (
    <>
      <Map
        ref={mapRef}
        mapContainerStyle={{
          width: '100%',
          height: '100%',
        }}
        zoom={zoomLabel}
        options={_googleMapOptions}
        onLoad={(map) => initGoogleMap(map)}
        onDragEnd={onDragMap}
        onZoomChanged={onChangeZoomMap}
      >
        {locationUnits.map((locationUnit, index) => {
          const mapMarker = isSpaceLocationUnit(locationUnit) ? locationUnit.mapMarker : null
          const iconUrl = getMarkerIcon(
            locationUnit.isAvailable,
            mapMarker,
            isLockerLocationUnit(locationUnit)
          )
          const isSelected =
            locationUnit.type === selectedLocationUnitId.type &&
            locationUnit.encryptedId === selectedLocationUnitId.encryptedId
          const iconHeight = isSelected ? 60 : 40
          const iconRatio = 0.8
          const coordinate = getCoordinate(locationUnit)

          return (
            <Marker
              position={{
                lat: coordinate.latitude,
                lng: coordinate.longitude,
              }}
              key={index}
              zIndex={getMarkerZIndex(index, isSelected, locationUnits.length)}
              icon={{
                url: iconUrl,
                scaledSize: new window.google.maps.Size(iconHeight * iconRatio, iconHeight),
              }}
              onClick={() => onPressMarker(locationUnit)}
            />
          )
        })}
        {(isMobile || isTablet) && selectedLocationUnit !== undefined && (
          <div className={styles.card}>
            <LocationUnitCard
              unit={selectedLocationUnit}
              selected={true}
              encryptedId={selectedLocationUnit.encryptedId}
            />
          </div>
        )}
      </Map>
    </>
  )
}

export default GoogleMap
